/* ======================================================================
   Sky & nature widgets. Same rules as app.css: rem sizes, design tokens,
   ≥ 48px touch targets, container queries for size (the .widget shell is a
   size container). Works in the dark "Lantern" and light "Daybreak" themes.
   ====================================================================== */

.sky {
  --aurora: #63d9a0;
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
  padding: var(--wp-t) var(--wp-x) var(--wp-b);
  overflow: hidden;
  position: relative;
  min-height: 0;
}
:root[data-theme="light"] .sky {
  --aurora: #1d8f5c;
}
.sky.is-compact {
  padding: var(--wp-t) var(--wp-x);
  justify-content: center;
}

/* Footer: source attribution + freshness badge (badge is absolutely placed bottom-right). */
.sky-foot {
  flex: none;
  margin-top: auto;
  display: flex;
  flex-wrap: wrap;
  gap: 0.2rem 0.75rem;
  align-items: center;
  font-size: 0.75rem;
  color: var(--text-3);
  border-top: 1px solid var(--line);
  padding-top: 0.45rem;
  padding-right: 7rem;
  min-height: 1.6rem;
}
.sky-foot .status-badge {
  bottom: 0.7rem;
}
.sky-source {
  max-width: 100%;
  line-height: 1.3;
}

.sky-note {
  margin: 0;
  font-size: 0.88rem;
  color: var(--text-2);
  line-height: 1.35;
}
.sky-lede {
  margin: 0;
  font-size: 1.1rem;
}
.sky-section-title {
  margin: 0 0 0.25rem;
  font-size: 0.75rem;
  font-weight: 650;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--text-3);
}

/* Stat blocks (label / big value / sub-line) */
.sky-stat {
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
  min-width: 0;
}
.sky-stat-label {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  font-size: 0.8rem;
  color: var(--text-2);
}
.sky-stat-value {
  font-size: 1.9rem;
  font-weight: 300;
  letter-spacing: -0.02em;
  line-height: 1.05;
  white-space: nowrap;
}
.sky-stat-sub {
  font-size: 0.8rem;
  color: var(--text-2);
}
.sky-stat.is-next .sky-stat-value {
  color: var(--sun);
}
.sky-stat.is-next .sky-stat-sub {
  color: var(--text);
}
.sky-big {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 2.4rem;
  font-weight: 250;
  letter-spacing: -0.02em;
  line-height: 1;
  white-space: nowrap;
}
.muted-num {
  color: var(--text-2);
}

/* Lists shared by night sky and quakes */
.sky-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
}
/* Icon, then name + detail, then the side note. When the row is too narrow
   for all of it, the side note wraps onto its own line instead of truncating
   anything. */
.sky-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  column-gap: 0.75rem;
  row-gap: 0.15rem;
  min-height: 3rem;
  padding: 0.35rem 0;
}
.sky-row > i {
  flex: none;
}
.sky-row + .sky-row {
  border-top: 1px solid var(--line);
}
.sky-row-main {
  display: flex;
  flex-direction: column;
  min-width: 0;
}
.sky-row > .sky-row-main {
  flex: 1 1 auto;
  max-width: calc(100% - 1.7rem);
}
.sky-row-main strong {
  font-weight: 550;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.sky-row-main .muted {
  font-size: 0.82rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.sky-row .sky-row-main strong,
.sky-row .sky-row-main .muted {
  white-space: normal;
  line-height: 1.3;
}
.sky-row-side {
  margin-left: auto;
  font-size: 0.8rem;
  color: var(--text-2);
  text-align: right;
  white-space: nowrap;
}
.sky-compact-line {
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  font-size: 1rem;
}
.sky-compact-line strong {
  font-size: 1.15rem;
  font-weight: 550;
}

/* ---------- Sun ---------- */
.sun-arc {
  flex: 1 1 5rem;
  min-height: 4.5rem;
  position: relative;
}
.sun-arc svg {
  position: absolute;
  inset: 0;
  display: block;
}
.sun-curve-night {
  fill: none;
  stroke: var(--line-strong);
  stroke-width: 2;
  stroke-dasharray: 3 5;
  stroke-linecap: round;
}
.sun-curve-day {
  fill: none;
  stroke: var(--sun);
  stroke-width: 2.5;
  stroke-linecap: round;
}
.sun-horizon {
  stroke: var(--line-strong);
  stroke-width: 1.5;
}
.sun-tick {
  stroke: var(--text-2);
  stroke-width: 2;
  stroke-linecap: round;
}
.sun-dot {
  fill: var(--sun);
  stroke: color-mix(in srgb, var(--sun) 40%, white);
  stroke-width: 2;
}
.sun-dot.below {
  fill: var(--card-solid);
  stroke: var(--text-3);
}
.sun-stats {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(7.5rem, 1fr));
  gap: 0.5rem 1rem;
  flex: none;
}
/* Daylight change and golden hour share a line when there's room. */
.sun-notes {
  flex: none;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.35rem 1.25rem;
}
.sun-notes:empty {
  display: none;
}
@container ((max-height: 380px) or (max-height: 21.4rem)) {
  .sun-arc {
    min-height: 3rem;
  }
}
.sky-delta {
  margin: 0;
  font-size: 0.92rem;
  color: var(--text);
  flex: none;
}
.sky-delta .up {
  color: var(--ok);
}
.sky-delta .down {
  color: var(--accent);
}
.sky-golden {
  margin: 0;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.88rem;
  color: var(--text-2);
  flex: none;
}
.sky-golden i {
  width: 0.65rem;
  height: 0.65rem;
  border-radius: 50%;
  background: linear-gradient(135deg, var(--sun), var(--accent));
}
.sky-golden.on {
  color: var(--sun);
  font-weight: 600;
}
.sky-sun.is-compact .sun-stats {
  grid-template-columns: 1fr 1fr;
}
.sky-sun.is-compact .sky-stat-value {
  font-size: 1.6rem;
}
.sky-sun.is-compact .sky-delta {
  font-size: 0.8rem;
  color: var(--text-2);
}

/* ---------- Moon ---------- */
.sky-moon {
  --moon-lit: #e9e2d2;
  --moon-lit-hi: #fffaf0;
  --moon-shadow: #1f1c19;
  --moon-maria: #8b8272;
}
:root[data-theme="light"] .sky-moon {
  --moon-shadow: #3d3832;
  --moon-lit: #efe7d6;
}
.moon-main {
  display: flex;
  align-items: center;
  gap: 1.1rem;
  flex: 1 1 auto;
  min-height: 0;
}
.moon-disc {
  height: 100%;
  max-height: 9rem;
  min-height: 4rem;
  aspect-ratio: 1;
  flex: none;
}
.moon-dark {
  fill: var(--moon-shadow);
}
.moon-rim {
  fill: none;
  stroke: color-mix(in srgb, var(--moon-lit) 22%, transparent);
  stroke-width: 1;
}
.moon-maria {
  fill: var(--moon-maria);
  opacity: 0.16;
}
.moon-text {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  min-width: 0;
}
.moon-name {
  font-size: 1.45rem;
  font-weight: 500;
  letter-spacing: -0.01em;
}
.moon-pct {
  font-size: 1rem;
  color: var(--text-2);
}
.moon-pct b {
  font-size: 2.2rem;
  font-weight: 250;
  color: var(--text);
  letter-spacing: -0.02em;
}
.moon-text .muted {
  font-size: 0.85rem;
}
.moon-events {
  margin: 0;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(8rem, 1fr));
  gap: 0.55rem 1rem;
  flex: none;
}
.moon-event dt {
  font-size: 0.78rem;
  color: var(--text-2);
}
.moon-event dd {
  margin: 0;
  display: flex;
  flex-direction: column;
}
.moon-event dd strong {
  font-weight: 500;
  font-size: 1.02rem;
  white-space: nowrap;
}
.moon-event dd .muted {
  font-size: 0.78rem;
}
.sky-moon.is-compact .moon-disc {
  max-height: 5rem;
}
.sky-moon.is-compact .moon-name {
  font-size: 1.1rem;
}
.sky-moon.is-compact .moon-pct b {
  font-size: 1.5rem;
}
@container ((max-width: 330px) or (max-width: 18.6rem)) and ((min-height: 300px) and (min-height: 16.9rem)) {
  .moon-main {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }
  .moon-disc {
    max-height: 6rem;
  }
}

/* ---------- Tonight's sky ---------- */
.night-sections {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  flex: 1;
  min-height: 0;
  overflow: hidden;
  /* Room for the planet dots' glow at the left edge. */
  padding-left: 0.3rem;
}
.sky-night.is-expanded .night-sections {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(16rem, 1fr));
  align-content: start;
  gap: 1rem 2rem;
}
.planet-dot {
  width: 0.9rem;
  height: 0.9rem;
  border-radius: 50%;
  background: radial-gradient(circle at 35% 35%, #fff, var(--planet) 55%);
  box-shadow: 0 0 0.35rem color-mix(in srgb, var(--planet) 60%, transparent);
}
.meteor-mark {
  width: 0.9rem;
  height: 0.9rem;
  background: linear-gradient(225deg, var(--text) 0 18%, transparent 70%);
  clip-path: polygon(100% 0, 30% 100%, 0 70%);
}
.iss-mark {
  width: 0.9rem;
  height: 0.9rem;
  border-radius: 2px;
  background: linear-gradient(90deg, var(--rain) 0 30%, var(--text) 30% 70%, var(--rain) 70%);
  transform: rotate(-30deg) scaleY(0.45);
}

/* ---------- Aurora ---------- */
.aurora-outlook {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  flex: none;
}
.aurora-chip {
  align-self: flex-start;
  display: inline-flex;
  align-items: center;
  min-height: 2.3rem;
  padding: 0.25rem 0.95rem;
  border-radius: 999px;
  font-weight: 600;
  font-size: 1.05rem;
  background: var(--raised);
  border: 1px solid var(--line);
  color: var(--text-2);
}
.level-slight .aurora-chip {
  color: var(--text);
}
.level-possible .aurora-chip {
  color: var(--warn);
  border-color: color-mix(in srgb, var(--warn) 45%, transparent);
  background: color-mix(in srgb, var(--warn) 12%, transparent);
}
.level-likely .aurora-chip,
.level-overhead .aurora-chip {
  color: var(--aurora);
  border-color: color-mix(in srgb, var(--aurora) 50%, transparent);
  background: color-mix(in srgb, var(--aurora) 14%, transparent);
}
.aurora-figures {
  display: flex;
  gap: 1.5rem;
  flex-wrap: wrap;
  flex: none;
}
.g-badge {
  font-size: 0.85rem;
  font-weight: 700;
  padding: 0.15rem 0.5rem;
  border-radius: 0.4rem;
  background: var(--danger-soft);
  color: var(--danger);
  letter-spacing: 0;
}
.kp-chart {
  flex: 1 1 4rem;
  min-height: 4rem;
  position: relative;
}
.kp-chart svg {
  position: absolute;
  inset: 0;
  display: block;
}
.kp-grid {
  stroke: var(--line);
  stroke-width: 1;
}
.kp-threshold {
  stroke: var(--aurora);
  stroke-width: 1.5;
  stroke-dasharray: 5 4;
}
.kp-now {
  stroke: var(--text);
  stroke-width: 1.5;
  opacity: 0.7;
}
.kp-bar.kp-quiet {
  fill: color-mix(in srgb, var(--ok) 75%, transparent);
  color: var(--ok);
}
.kp-bar.kp-active {
  fill: var(--warn);
  color: var(--warn);
}
.kp-bar.kp-storm {
  fill: var(--danger);
  color: var(--danger);
}
.kp-bar.is-forecast {
  fill-opacity: 0.35;
  stroke: currentColor;
  stroke-width: 1;
  stroke-dasharray: 2 2;
}
.kp-label {
  fill: var(--text-2);
  font-size: 0.72rem;
}

/* ---------- UV ---------- */
.uv-main {
  display: flex;
  align-items: center;
  gap: 1.1rem;
  flex: none;
}
.uv-badge {
  display: grid;
  place-items: center;
  width: 5.2rem;
  height: 5.2rem;
  flex: none;
  border-radius: 50%;
  background: radial-gradient(circle, color-mix(in srgb, var(--uv) 30%, transparent) 0 58%, transparent 60%);
  border: 0.42rem solid var(--uv);
}
.uv-num {
  font-size: 2.5rem;
  font-weight: 300;
  letter-spacing: -0.03em;
}
.uv-text {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  min-width: 0;
}
.uv-text strong {
  font-size: 1.3rem;
  font-weight: 550;
}
.uv-text .muted {
  font-size: 0.88rem;
}
.uv-advice {
  font-size: 0.85rem;
  color: var(--text-2);
}
.uv-bars {
  list-style: none;
  margin: 0;
  padding: 0;
  flex: 1 1 4rem;
  min-height: 4rem;
  max-height: 9rem;
  margin-top: auto;
  display: flex;
  align-items: stretch;
  gap: 0.25rem;
}
.uv-bars li {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;
  gap: 0.15rem;
  min-width: 0;
}
.uv-bar {
  width: 70%;
  max-width: 1.6rem;
  border-radius: 0.35rem 0.35rem 0.15rem 0.15rem;
  background: var(--uv);
}
.uv-bar-val {
  font-size: 0.72rem;
  color: var(--text-2);
}
.uv-bar-hour {
  font-size: 0.7rem;
  color: var(--text-3);
}
.uv-bars li.past {
  opacity: 0.45;
}
.uv-bars li.now .uv-bar-hour,
.uv-bars li.now .uv-bar-val {
  color: var(--text);
  font-weight: 700;
}
.uv-bars li.now .uv-bar {
  box-shadow: 0 0 0 2px var(--card-solid), 0 0 0 3.5px var(--text);
}
.sky-uv.is-compact .uv-badge {
  width: 3.8rem;
  height: 3.8rem;
  border-width: 0.32rem;
}
.sky-uv.is-compact .uv-num {
  font-size: 1.8rem;
}
@container ((max-width: 300px) or (max-width: 16.9rem)) {
  .uv-bar-val {
    display: none;
  }
}

/* ---------- Earthquakes ---------- */
.quake-list {
  list-style: none;
  margin: 0;
  padding: 0;
  flex: 1;
  min-height: 0;
  overflow: hidden;
}
.quake-row {
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: center;
  gap: 0.8rem;
  min-height: 3.2rem;
  padding: 0.25rem 0;
}
.quake-row + .quake-row {
  border-top: 1px solid var(--line);
}
.quake-mag {
  display: grid;
  place-items: center;
  width: 2.9rem;
  height: 2.3rem;
  border-radius: 0.6rem;
  font-weight: 700;
  font-size: 1.05rem;
  background: var(--raised-2);
  color: var(--text);
}
.quake-mag.mag-3 {
  background: color-mix(in srgb, var(--sun) 24%, transparent);
  color: var(--sun);
}
.quake-mag.mag-4 {
  background: color-mix(in srgb, var(--accent) 26%, transparent);
  color: var(--accent);
}
.quake-mag.mag-5,
.quake-mag.mag-6 {
  background: var(--danger);
  color: #fff;
}
.sky-quakes.is-compact .quake-row .sky-row-main .muted {
  display: none;
}

/* ---------- Hourly graph ---------- */
.hourly-graph {
  flex: 1;
  min-height: 6rem;
  position: relative;
}
.hourly-graph svg {
  position: absolute;
  inset: 0;
  display: block;
  overflow: visible;
}
.hg-line {
  fill: none;
  stroke: var(--accent);
  stroke-width: 2.5;
  stroke-linecap: round;
  stroke-linejoin: round;
}
.hg-dot {
  fill: var(--card-solid);
  stroke: var(--accent);
  stroke-width: 2;
}
.hg-dot.hg-extreme {
  fill: var(--accent);
}
.hg-temp {
  fill: var(--text);
  font-size: 0.9rem;
  font-weight: 550;
  text-anchor: middle;
}
.hg-hour {
  fill: var(--text-2);
  font-size: 0.75rem;
  text-anchor: middle;
}
.hg-pop {
  fill: var(--rain);
}
.hg-pop-label {
  fill: var(--rain);
  font-size: 0.7rem;
  font-weight: 650;
  text-anchor: middle;
}
.hg-base {
  stroke: var(--line);
  stroke-width: 1;
}
.kp-threshold-label {
  fill: var(--aurora);
  text-anchor: end;
}

.sky-night.is-expanded .sky-row-main .muted {
  white-space: normal;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}
/* Wide but not tall: put sections side by side instead of clipping the last
   one. Two columns: planets on the left, the shorter sections stacked right. */
@container ((min-width: 600px) and (min-width: 33.8rem)) {
  .sky-night .night-sections {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    align-content: start;
    gap: 0.75rem 1.75rem;
  }
  .sky-night .night-sections > .night-section:first-child:not(:last-child) {
    grid-row: span 2;
  }
}
@container ((min-width: 960px) and (min-width: 54.1rem)) {
  .sky-night .night-sections {
    grid-template-columns: repeat(auto-fit, minmax(16rem, 1fr));
  }
  .sky-night .night-sections > .night-section:first-child:not(:last-child) {
    grid-row: auto;
  }
}

/* Short moon cards (e.g. 3 rows on the Weather & sky screen): one row of
   events, no secondary lines, so nothing overlaps the disc. */
@container ((max-height: 400px) or (max-height: 22.5rem)) {
  .moon-events {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.3rem 0.75rem;
  }
  .moon-event dd .muted,
  .moon-text .muted {
    display: none;
  }
  .moon-disc {
    min-height: 3rem;
  }
  .moon-pct b {
    font-size: 1.7rem;
  }
}
@container ((max-height: 400px) or (max-height: 22.5rem)) and ((min-width: 520px) and (min-width: 29.3rem)) {
  .moon-events {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
}
